import clsx from "clsx";
import { type ElementType, type ReactNode, useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import { typedObjectEntries } from "common/helpers/utils";

type EditOptionsType = Record<
  string,
  { icon: ElementType; label: string; isDanger?: true }
>;

interface MobileDetailsDrawerProps<EditOptions extends EditOptionsType> {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  children: ReactNode;
  editOptions: EditOptions;
  renderEditComponent: (
    editKey: keyof EditOptions,
    onBack: () => void,
  ) => JSX.Element;
  editTitle: string;
}

export default function MobileDetailsDrawer<
  EditOptions extends EditOptionsType,
>({
  isOpen,
  setIsOpen,
  children,
  editOptions,
  renderEditComponent,
  editTitle,
}: MobileDetailsDrawerProps<EditOptions>) {
  const [isEditingMenuOpen, setIsEditingMenuOpen] = useState(false);
  const [editKey, setEditKey] = useState<keyof EditOptions>();

  let content = (
    <>
      <DrawerBody>{children}</DrawerBody>
      <DrawerFooter className="justify-between">
        <DrawerClose asChild>
          <Button variant="secondary-black">Cancel</Button>
        </DrawerClose>
        {!!Object.keys(editOptions).length && (
          <Button onClick={() => setIsEditingMenuOpen(true)}>Edit</Button>
        )}
      </DrawerFooter>
    </>
  );
  if (isEditingMenuOpen) {
    content = (
      <>
        <DrawerHeader title={editTitle} />
        <DrawerBody>
          {typedObjectEntries(editOptions).map(
            ([key, { icon: Icon, label, isDanger }]) => {
              return (
                <button
                  key={String(key)}
                  type="button"
                  onClick={() => setEditKey(key)}
                  className={clsx(
                    "flex items-center p-2 border-b border-b-black-100 last:border-b-0 gap-x-1.5 text-button-sm",
                    isDanger ? "text-red-500" : "",
                  )}
                >
                  <Icon className="size-6" weight="fill" />
                  {label}
                </button>
              );
            },
          )}
        </DrawerBody>
      </>
    );
  }
  if (editKey) {
    content = renderEditComponent(editKey, () => setEditKey(undefined));
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={setIsOpen}
      onAfterClose={() => {
        setEditKey(undefined);
        setIsEditingMenuOpen(false);
      }}
    >
      <DrawerContent>{content}</DrawerContent>
    </Drawer>
  );
}
